import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Users, CheckCircle2, DollarSign, Download } from "lucide-react";
import { api, fmtPrice } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

const TIER_NAMES = { 1: "Bronze", 2: "Silver", 3: "Gold" };

export default function PartnersTab() {
  const [partners, setPartners] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingId, setMarkingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, po] = await Promise.all([
        api.get("/admin/partners"),
        api.get("/admin/partner-payouts"),
      ]);
      setPartners(p.data);
      setPayouts(po.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markPaid = async (id) => {
    setMarkingId(id);
    try {
      await api.post(`/admin/partner-payouts/${id}/mark-paid`);
      toast.success("Payout marked as paid — partner notified");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setMarkingId(null);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#4ADE80]" /></div>;

  const pendingCount = payouts.filter((p) => p.status === "requested").length;

  return (
    <div className="space-y-8">
      {/* Pending payouts */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-[#4ADE80]" />
            <span className="font-display text-lg">Payouts requested</span>
            {pendingCount > 0 && <Badge className="bg-[#4ADE80]/20 text-[#4ADE80] border-0">{pendingCount}</Badge>}
          </div>
          <Button
            data-testid="admin-export-referrals-csv"
            variant="ghost"
            onClick={async () => {
              try {
                const res = await api.get("/admin/referrals/export.csv", { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url; a.download = "endag1-pending-referrals.csv";
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast.success("Exported pending referrals");
              } catch (e) {
                toast.error(e.response?.data?.detail || "Export failed");
              }
            }}
            className="border border-white/20 text-[#F5F5F5] hover:border-[#4ADE80] hover:text-[#4ADE80]"
          >
            <Download className="h-4 w-4 mr-2" /> Export pending CSV
          </Button>
        </div>
        <div className="border border-white/10 bg-[#1F1F1F] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.2em] text-[#A8A8A8]">
              <tr className="border-b border-white/10">
                <th className="text-left px-4 py-3">Requested</th>
                <th className="text-left px-4 py-3">Partner ID</th>
                <th className="text-left px-4 py-3">Method</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-right px-4 py-3">Refs</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {payouts.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-[#A8A8A8]">No payouts yet.</td></tr>
              )}
              {payouts.map((p) => (
                <tr key={p.id} data-testid={`payout-row-${p.id}`} className="border-b border-white/5">
                  <td className="px-4 py-3 text-[#A8A8A8]">{(p.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{(p.partner_user_id || "").slice(0, 10)}…</td>
                  <td className="px-4 py-3 text-[#A8A8A8]">{p.method}</td>
                  <td className="px-4 py-3">{p.email}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#4ADE80]">{fmtPrice(p.amount)}</td>
                  <td className="px-4 py-3 text-right">{p.referral_count || 0}</td>
                  <td className="px-4 py-3 text-center">
                    {p.status === "paid"
                      ? <Badge className="bg-[#4ADE80]/20 text-[#4ADE80] border-0">Paid</Badge>
                      : <Badge className="bg-yellow-500/20 text-yellow-400 border-0">Requested</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {p.status === "requested" && (
                      <Button
                        data-testid={`mark-paid-${p.id}`}
                        size="sm"
                        onClick={() => markPaid(p.id)}
                        disabled={markingId === p.id}
                        className="bg-[#4ADE80] text-[#0F0F10] hover:bg-[#6FE998]"
                      >
                        {markingId === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                        Mark paid
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* All partners */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-4 w-4 text-[#4ADE80]" />
          <span className="font-display text-lg">All partners</span>
          <span className="text-sm text-[#A8A8A8]">{partners.length}</span>
        </div>
        <div className="border border-white/10 bg-[#1F1F1F] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.2em] text-[#A8A8A8]">
              <tr className="border-b border-white/10">
                <th className="text-left px-4 py-3">Partner</th>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Tier</th>
                <th className="text-right px-4 py-3">Refs</th>
                <th className="text-right px-4 py-3">Lifetime</th>
                <th className="text-right px-4 py-3">Pending</th>
                <th className="text-right px-4 py-3">Paid out</th>
                <th className="text-left px-4 py-3">Payout</th>
              </tr>
            </thead>
            <tbody>
              {partners.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-[#A8A8A8]">No partners enrolled yet.</td></tr>
              )}
              {partners.map((p) => (
                <tr key={p.user_id} data-testid={`partner-row-${p.partner_code}`} className="border-b border-white/5">
                  <td className="px-4 py-3">
                    <div className="text-[#F5F5F5]">{p.name || "—"}</div>
                    <div className="text-xs text-[#A8A8A8]">{p.email}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-[#4ADE80]">{p.partner_code}</td>
                  <td className="px-4 py-3 text-xs">{TIER_NAMES[p.partner_tier] || "Bronze"}</td>
                  <td className="px-4 py-3 text-right">{p.total_refs || 0}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmtPrice(p.lifetime_earned || 0)}</td>
                  <td className="px-4 py-3 text-right font-mono text-yellow-400">{fmtPrice(p.pending_balance || 0)}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#4ADE80]">{fmtPrice(p.paid_balance || 0)}</td>
                  <td className="px-4 py-3 text-xs text-[#A8A8A8]">
                    {p.partner_payout_method ? `${p.partner_payout_method} · ${p.partner_payout_email || "—"}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
